"use client";

import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useState, useEffect } from "react";
import { CustomButton } from "@/components/ui/custom-button";
import { ReadMoreButton } from "@/components/ui/read-more-button";

interface EventItem {
	title: string;
	text: string;
	imageUrl: string;
	link: string;
}

interface DynamicEventsProps {
	items: EventItem[];
	className?: string;
	showViewAll?: boolean;
	showKeepReading?: boolean;
}

export function DynamicEvents({ items, className = "", showViewAll = true, showKeepReading = false }: DynamicEventsProps) {
	const [itemsToShow, setItemsToShow] = useState(9);
	const [newlyLoadedItems, setNewlyLoadedItems] = useState<Set<number>>(new Set());
	const hasMoreItems = itemsToShow < items.length;

	const handleLoadMore = () => {
		const previousCount = itemsToShow;
		const newCount = Math.min(itemsToShow + 6, items.length);
		
		// Track which items are newly loaded
		const newItems = new Set<number>();
		for (let i = previousCount; i < newCount; i++) {
			newItems.add(i);
		}
		setNewlyLoadedItems(newItems);
		
		setItemsToShow(newCount);
	};

	// Clear the newly loaded items after animation completes
	useEffect(() => {
		if (newlyLoadedItems.size > 0) {
			const timer = setTimeout(() => {
				setNewlyLoadedItems(new Set());
			}, 600); // Match animation duration
			return () => clearTimeout(timer);
		}
	}, [newlyLoadedItems]);

	return (
		<section className={`py-10 px-6 ${className} ${showKeepReading ? 'pb-32 md:pb-64' : ''}`}>
			<div className="max-w-7xl mx-auto">
				{/* Header with Keep Reading or View all button */}
				{showKeepReading ? (
					<div className="mb-16">
						<h2 className="text-3xl font-bold text-gray-900 md:text-4xl">
							Keep Reading
						</h2>
					</div>
				) : showViewAll && (
					<div className="flex justify-end mb-8">
						<Link href="/about/events-webinars">
							<CustomButton variant="outline" size="lg">
								View all
							</CustomButton>
						</Link>
					</div>
				)}
				
				{/* Events grid */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12 sm:gap-y-24">
					{items.slice(0, itemsToShow).map((item, index) => {
						const isNewlyLoaded = newlyLoadedItems.has(index);
						const isExternalLink = item.link.startsWith('http://') || item.link.startsWith('https://');
						
						const cardContent = (
							<div className="bg-white transition-all duration-300 ease-in-out group-hover:scale-[1.02] h-full flex flex-col">
								{/* Image */}
								<div className="relative h-64 w-full overflow-hidden rounded-3xl">
									<Image
										src={item.imageUrl}
										alt={item.title}
										fill
										className="object-cover group-hover:scale-105 transition-transform duration-300"
									/>
								</div>
								
								{/* Content */}
								<div className="pt-4 flex-1 flex flex-col">
									<h5 className="mb-1 group-hover:text-ss-blue transition-colors text-xl font-semibold text-gray-900">
										{item.title}
									</h5>
									<p className="text-sm leading-relaxed max-h-20 overflow-hidden flex-1">
										{item.text}
									</p>
									<div className="mt-6">
										<ReadMoreButton showArrow={false} size="lg" />
									</div>
								</div>
							</div>
						);
						
						return (
							<div
								key={index}
								className={`group block h-full transition-all duration-600 ease-out ${
									isNewlyLoaded 
										? 'animate-fade-in-up opacity-0' 
										: 'opacity-100'
								}`}
								style={{
									animationDelay: isNewlyLoaded ? `${(index % 6) * 100}ms` : '0ms',
									animationFillMode: 'forwards'
								}}
							>
								{isExternalLink ? (
									<a 
										href={item.link}
										target="_blank"
										rel="noopener noreferrer"
										className="block h-full"
									>
										{cardContent}
									</a>
								) : (
									<Link href={item.link} className="block h-full">
										{cardContent}
									</Link>
								)}
							</div>
						);
					})}
				</div>

				{/* Mobile View all button */}
				{showViewAll && (
					<div className="md:hidden mt-8 text-center">
						<Link href="/about/events-webinars">
							<CustomButton variant="outline" size="lg">
								View all
							</CustomButton>
						</Link>
					</div>
				)}
				
				{/* Load more button - centered at bottom */}
				{items.length > 9 && hasMoreItems && (
					<div className="mt-24 text-center">
						<CustomButton 
							variant="outline" 
							size="lg"
							onClick={handleLoadMore}
						>
							Load more events
						</CustomButton>
					</div>
				)}
			</div>
		</section>
	);
}
